import {formatNumberWithCommas} from '../../../formatters/formatNumberWithCommas'
import {Nullable} from '../../../app/TypeHelpers'
import {AgentReservationInfo, ReservationInfo} from './AdminReserveInterfaces'
import {PaymentInformation, PaymentItem} from '../ReserveFacilityModel'
import {ResidentUserInfo} from '../../../__NoAutomatedTests/components/roomToResident/repository/NatGetRoomToResident'
import React from 'react'
import {ReservationClass} from '../ReservationInterfaces'
import {getStringOrEmptyString} from '../../../__NoAutomatedTests/common/util/Natutil'

export interface PaymentViewData {
    article: string
    charge: string
    quantityWithUnit: string
    paymentType: string
}

export function createPaymentViewData(
    paymentItems: PaymentItem[],
    reservationInfo: ReservationInfo,
): PaymentViewData[] {
    return paymentItems.map((info) => {
        if (info.quantityWithUnit === null || info.charge === null) {
            return
        }

        if (info.id === null) {
            let paymentTypeString = ''
            if (reservationInfo.classification === ReservationClass.AGENT) {
                if (reservationInfo.paymentType === null) {
                    throw Error('支払い情報がありません。')
                }

                paymentTypeString = reservationInfo.paymentType.label
            }

            return {
                article: info.article,
                charge: `${formatNumberWithCommas(info.charge)}円`,
                quantityWithUnit: '',
                paymentType: paymentTypeString,
            }
        }

        return {
            article: info.article,
            charge: `${formatNumberWithCommas(info.charge)}円`,
            quantityWithUnit: info.quantityWithUnit,
            paymentType: info.paymentType.label,
        }
    }).filter(item => item !== undefined) as PaymentViewData[]
}

export function updatePaymentInfo(
    paymentInfo: PaymentInformation, reservationInfo: AgentReservationInfo,
): Nullable<PaymentInformation> {

    const {items, totalCharge} = paymentInfo
    if (items.length === 0) {
        return null
    }

    const {paymentType: paymentTypeForFacility, reservationOptionInfos} = reservationInfo

    const replaced: PaymentItem[] = items.map(item => {
        const forFacility = (item.id === null)
        if (forFacility && paymentTypeForFacility !== null) {
            return {...item, paymentType: paymentTypeForFacility}
        }

        if (reservationOptionInfos === null) {
            throw Error('予約オプション情報がありません')
        }
        const reserveOption = reservationOptionInfos.find(option => option.optionId === item.id)
        if (reserveOption !== undefined) {
            return {...item, paymentType: reserveOption.paymentType}
        } else {
            throw Error('予約オプション情報とcalculatorの結果のアイテム数が一致しません')
        }
    })

    return {items: replaced, totalCharge}
}

export function getDisplayRoomNumber(userInfo: ResidentUserInfo): string {
    return `${userInfo.ridgeName} ${userInfo.roomNumber}`
}

export function getDisplayUserName(userInfo: ResidentUserInfo): string {
    return createUserName(userInfo.familyName, userInfo.firstName)
}

export function getDisplayUserNameForManagement(userInfo: ResidentUserInfo): string {
    let displayUserName: string
    if (hasParentUser(userInfo)) {
        displayUserName = createUserName(userInfo.parentAdminFamilyName, userInfo.parentAdminFirstName)
    } else {
        displayUserName = createUserName(userInfo.adminFamilyName, userInfo.adminFirstName)
    }

    return displayUserName
}

export function getDisplaySelectedUserOrRoleName(userInfo: ResidentUserInfo): string {
    let displaySelectedUserName: string
    if (hasParentUser(userInfo)) {
        displaySelectedUserName = createUserName(userInfo.parentFamilyName, userInfo.parentFirstName)
    } else {
        displaySelectedUserName = createUserName(userInfo.familyName, userInfo.firstName, userInfo.roleName)
    }

    return displaySelectedUserName
}

export function getDisplaySelectedUserName(userInfo: ResidentUserInfo): string {
    let displaySelectedUserName: string
    if (hasParentUser(userInfo)) {
        displaySelectedUserName = createUserName(userInfo.parentFamilyName, userInfo.parentFirstName)
    } else {
        displaySelectedUserName = createUserName(userInfo.familyName, userInfo.firstName)
    }

    return displaySelectedUserName
}

function hasParentUser(userInfo: ResidentUserInfo): boolean {
    return userInfo.parentFamilyName !== null
}

function createUserName(familyName: Nullable<string>, firstName: Nullable<string>, defaultName?: string): string {
    const userName = `${getStringOrEmptyString(familyName)} ${getStringOrEmptyString(firstName)}`.trim()
    if (defaultName !== undefined && userName === '') {
        return defaultName
    }
    return userName
}
